import { clear, getItem, setItem } from '@/utils/localstorage';
import { Message } from '@/utils/message';
import { showLoading, hideLoadingAfterRender } from '@/utils/loading';
import axios, { AxiosRequestConfig, Canceler } from 'axios';

axios.defaults.timeout = 120000;
axios.defaults.baseURL = '/flagos/api/v1';
import jsonBig from 'json-bigint';
import { LocaleTypeEnum } from '@/store/locale';
import { getCookie } from '@/utils/cookies';
import { goLogin } from '@/utils/commonFun';
// 跨域请求时携带 Cookie
axios.defaults.withCredentials = true;
/**
 * 根据当前系统语言获取语言代码
 * 中文: CN, 英文: EN
 */
export const getLangCode = (currentLocale: string): string => {
    if (currentLocale === LocaleTypeEnum.Chinese) {
        return 'CN';
    }
    return 'EN';
};

axios.defaults.transformResponse = [
    data => {
        try {
            // 确保 data 是字符串类型，jsonBig.parse 需要字符串参数
            if (typeof data === 'string') {
                // 使用 json-bigint 解析，storeAsString: true 会将大数字转换为字符串
                return jsonBig({ storeAsString: true }).parse(data);
            }
            // 如果 data 已经是对象，直接返回（可能已经被其他 transformResponse 处理过）
            return data;
        } catch (err) {
            // 如果 json-bigint 转换失败，尝试使用原生 JSON.parse 作为降级方案
            // 这种情况通常发生在数据格式异常时
            try {
                if (typeof data === 'string') {
                    return JSON.parse(data);
                }
                return data;
            } catch (parseErr) {
                // 如果都失败了，返回原始数据，避免因解析错误导致请求失败
                return data;
            }
        }
    },
];

axios.interceptors.request.use(
    config => {
        let token = '';
        try {
            token = getCookie('baai-data-admin-token-key') || '';
        } catch (e) {}
        // 确保 headers 存在
        config.headers = config.headers || {};
        config.headers['Authorization'] = 'Bearer ' + token;
        const { getCurrentLocale } = useStore('locale');
        config.headers['Lang'] = getLangCode(getCurrentLocale.value);
        return config;
    },
    error => {
        return Promise.reject(error.response);
    }
);
/**
 * 请求响应拦截
 * 用于处理需要在请求返回后的操作
 */
axios.interceptors.response.use(
    response => {
        const responseCode = response.status;
        // 如果返回的状态码为200，说明接口请求成功，可以正常拿到数据
        // 否则的话抛出错误
        if (responseCode >= 200 && responseCode < 300) {
            return Promise.resolve(response);
        } else {
            return Promise.reject(response);
        }
    },
    error => {
        return Promise.reject(error);
    }
);

export async function cancelRequest(config: AxiosRequestConfig, hasErrTips = false) {
    // 创建一个取消令牌
    const CancelToken = axios.CancelToken;
    let cancel: Canceler | undefined;

    const axioRequest = async () => {
        return await request(
            {
                ...config,
                cancelToken: new CancelToken(c => (cancel = c)),
            },
            hasErrTips
        ).finally(() => {
            cancel = undefined;
        });
    };
    const cancelAxioRequest = () => cancel && cancel('Operation canceled by the user.');
    return {
        axioRequest,
        cancelAxioRequest,
    };
}

export async function request(config: AxiosRequestConfig, hasErrTips = true, isJumpLogin = true, showLoadingFlag = false) {
    if (showLoadingFlag) {
        showLoading();
    }

    try {
        const data = await axios({
            ...config,
            params: {
                ...(config.params || {}),
            },
        });
        return data.data;
    } catch (e: any) {
        const message = e.message;

        if (hasErrTips && message) {
            Message.error(e.response?.data?.msg || e.response?.data?.message || e.response?.data?.detail?.msg || e.message);
        }

        if (axios.isCancel(e)) {
            return Promise.reject('Request canceled' + message);
        }
        const { clearUser } = useStore('user');
        if (e?.response?.status === 401) {
            clearUser();
            const { getCurrentLocale } = useStore('locale');
            const localeBeforeLogin = getCurrentLocale.value;
            clear();
            if (isJumpLogin) {
                if (!location.pathname.endsWith('/login')) {
                    setItem('flagos_locale_before_login', localeBeforeLogin);
                }
                goLogin();
            }
            // 401 错误也应该被抛出，而不是返回 undefined
            return Promise.reject(new Error(message || 'Unauthorized'));
        }

        return Promise.reject(new Error(message));
    } finally {
        if (showLoadingFlag) {
            hideLoadingAfterRender();
        }
    }
}
